
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  PlusCircle, 
  Play, 
  Calendar, 
  BarChart, 
  Target, 
  Clock, 
  TrendingUp, 
  Users, 
  MessageCircle, 
  Share2,
  FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Sample data for active campaigns
const activeCampaigns = [
  {
    id: 1,
    title: "Summer Album Release",
    type: "album",
    status: "active",
    progress: 65,
    daysLeft: 14,
    reach: 24580,
    engagement: 1876,
    platforms: ["instagram", "tiktok", "spotify"],
    tasks: { completed: 12, total: 18 }
  },
  {
    id: 2,
    title: "US Tour Promotion",
    type: "tour",
    status: "active",
    progress: 32,
    daysLeft: 28,
    reach: 15230,
    engagement: 948,
    platforms: ["instagram", "facebook", "twitter"],
    tasks: { completed: 8, total: 24 }
  },
  {
    id: 3,
    title: "New Music Video",
    type: "video",
    status: "draft",
    progress: 0,
    daysLeft: 45,
    reach: 0,
    engagement: 0,
    platforms: ["youtube", "instagram", "tiktok"],
    tasks: { completed: 0, total: 16 }
  }
];

// Sample data for campaign metrics
const campaignMetrics = [
  { label: "Total Campaigns", value: 8, icon: <Target className="h-4 w-4" />, trend: "+2" },
  { label: "Total Reach", value: "86.4K", icon: <Users className="h-4 w-4" />, trend: "+12.5%" },
  { label: "Engagement Rate", value: "4.2%", icon: <MessageCircle className="h-4 w-4" />, trend: "+0.8%" },
  { label: "Shares", value: "1,248", icon: <Share2 className="h-4 w-4" />, trend: "+22%" }
];

const CampaignDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Campaign Dashboard</h2>
          <p className="text-muted-foreground">Manage and track your active marketing campaigns</p>
        </div>
        <Button onClick={() => navigate('?tab=planner&new=true')} className="gap-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700">
          <PlusCircle className="h-4 w-4" />
          New Campaign
        </Button>
      </div>
      
      {/* Campaign Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {campaignMetrics.map((metric, index) => (
          <Card key={index} className="bg-card/40 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                  {metric.icon}
                  <span>{metric.label}</span>
                </div>
                <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                  {metric.trend}
                </Badge>
              </div>
              <div className="mt-2 text-3xl font-bold">{metric.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Active Campaigns */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Active Campaigns</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {activeCampaigns.map((campaign) => (
            <Card key={campaign.id} className="overflow-hidden transition-all hover:shadow-md hover:border-purple-500/50">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{campaign.title}</CardTitle>
                    <CardDescription>
                      {campaign.type.charAt(0).toUpperCase() + campaign.type.slice(1)} Campaign
                    </CardDescription>
                  </div>
                  <Badge 
                    variant={campaign.status === 'active' ? 'default' : 'outline'}
                    className={campaign.status === 'active' 
                      ? 'bg-green-500/80 hover:bg-green-500' 
                      : 'text-muted-foreground'
                    }
                  >
                    {campaign.status.toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Progress</span>
                      <span>{campaign.progress}%</span>
                    </div>
                    <Progress value={campaign.progress} className="h-2" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Time Left
                      </span>
                      <span className="font-medium">{campaign.daysLeft} days</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" /> Reach
                      </span>
                      <span className="font-medium">{campaign.reach.toLocaleString()}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" /> Engagement
                      </span>
                      <span className="font-medium">{campaign.engagement.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between pt-2">
                <div className="flex gap-1">
                  {campaign.platforms.map((platform, index) => (
                    <Badge key={index} variant="outline" className="bg-muted/50">
                      {platform}
                    </Badge>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`gap-1 ${campaign.status === 'draft' ? 'text-purple-500' : 'text-green-500'}`}
                >
                  {campaign.status === 'draft' ? (
                    <>
                      <Play className="h-3 w-3" /> Start
                    </>
                  ) : (
                    <>
                      <BarChart className="h-3 w-3" /> Details
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
          
          {/* Create New Campaign Card */}
          <Card className="border-dashed border-muted-foreground/20 bg-muted/20 flex items-center justify-center h-[250px] cursor-pointer hover:bg-muted/40 transition-colors">
            <Button 
              variant="ghost" 
              className="gap-2 text-muted-foreground"
              onClick={() => navigate('?tab=planner&new=true')}
            >
              <PlusCircle className="h-5 w-5" />
              Create New Campaign
            </Button>
          </Card>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button variant="outline" className="h-auto flex-col p-4 gap-2 justify-start items-center hover:bg-purple-500/10 hover:text-purple-500 hover:border-purple-500/30">
            <Calendar className="h-6 w-6" />
            <span>Schedule Posts</span>
          </Button>
          <Button variant="outline" className="h-auto flex-col p-4 gap-2 justify-start items-center hover:bg-purple-500/10 hover:text-purple-500 hover:border-purple-500/30">
            <FileText className="h-6 w-6" />
            <span>Create Template</span>
          </Button>
          <Button variant="outline" className="h-auto flex-col p-4 gap-2 justify-start items-center hover:bg-purple-500/10 hover:text-purple-500 hover:border-purple-500/30">
            <Target className="h-6 w-6" />
            <span>Audience Analytics</span>
          </Button>
          <Button variant="outline" className="h-auto flex-col p-4 gap-2 justify-start items-center hover:bg-purple-500/10 hover:text-purple-500 hover:border-purple-500/30">
            <BarChart className="h-6 w-6" />
            <span>Performance Metrics</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboard;
